import { useState, useEffect } from 'react'
import zhMessages from '../messages/zh'
import enMessages from '../messages/en'
import idMessages from '../messages/id'

// 语言类型定义
export type Locale = 'zh' | 'en' | 'id'

// 多语言消息类型
export interface Messages {
  [key: string]: any
}

// 多语言上下文类型
export interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  tRaw: (key: string) => any
  isLoading: boolean
}

// 默认语言
const DEFAULT_LOCALE: Locale = 'zh'

// 支持的语言列表
const SUPPORTED_LOCALES: Locale[] = ['zh', 'en', 'id']

// 语言检测
const detectLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  
  // 首先检查 localStorage 中保存的语言
  const savedLocale = localStorage.getItem('locale') as Locale
  if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
    return savedLocale
  }
  
  // 检查浏览器语言设置
  const browserLanguage = navigator.language || navigator.languages?.[0] || 'en'
  
  // 如果浏览器语言是中文相关，返回中文
  if (browserLanguage.includes('zh') || browserLanguage.includes('cn')) {
    return 'zh'
  }
  
  // 如果浏览器语言是印尼语相关，返回印尼语
  if (browserLanguage.includes('id') || browserLanguage.includes('in')) {
    return 'id'
  }
  
  // 如果浏览器语言是英文相关，返回英文
  if (browserLanguage.includes('en')) {
    return 'en'
  }
  
  // 默认返回中文
  return DEFAULT_LOCALE
}

// 加载语言文件
const loadMessages = async (locale: Locale): Promise<Messages> => {
  const messageMap = {
    zh: zhMessages,
    en: enMessages,
    id: idMessages
  }
  
  return messageMap[locale] || messageMap[DEFAULT_LOCALE]
}

// 获取嵌套属性值
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined
  }, obj)
}

// 多语言 Hook
export const useI18n = () => {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale())
  const [messages, setMessages] = useState<Messages>(() => {
    // 立即加载默认语言的消息
    const detectedLocale = detectLocale()
    const messageMap = {
      zh: zhMessages,
      en: enMessages,
      id: idMessages
    }
    return messageMap[detectedLocale] || messageMap[DEFAULT_LOCALE]
  })
  const [isLoading, setIsLoading] = useState(false)

  // 设置语言
  const setLocale = async (newLocale: Locale) => {
    if (newLocale === locale) return
    
    setIsLoading(true)
    try {
      const newMessages = await loadMessages(newLocale)
      setMessages(newMessages)
      setLocaleState(newLocale)
      
      // 保存到 localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('locale', newLocale)
      }
    } catch (error) {
      console.error('Failed to load messages:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // 翻译函数
  const t = (key: string): string => {
    const value = getNestedValue(messages, key)
    return typeof value === 'string' ? value : key
  }

  // 获取原始值（用于数组或对象）
  const tRaw = (key: string): any => {
    return getNestedValue(messages, key)
  }

  // 初始化
  useEffect(() => {
    const initI18n = async () => {
      const detectedLocale = detectLocale()
      
      // 如果检测到的语言与当前语言不同，则切换
      if (detectedLocale !== locale) {
        await setLocale(detectedLocale)
      }
    }
    
    initI18n()
  }, [])

  return {
    locale,
    setLocale,
    t,
    tRaw,
    isLoading
  }
}

// 导出类型和常量
export { DEFAULT_LOCALE, SUPPORTED_LOCALES }
